/* import __COLOCATED_TEMPLATE__ from './sq-popover.hbs'; */
/**
`sq-popover` renders its yielded contents inside the basic popover style - a box with drop shadow
that requires attention. It's currently used within and inherited by many dashboard/glass-ui
components such as [sq-dropdown](/components/glass/sq-dropdown.html) and
[sq-tooltip](/components/dashboard/sq-tooltip.html).

```example
@title Basic Example

@hbs
{{#sq-popover class="popover--fly-up pointer--bottom"
              isActive=true}}
  Contents inside
{{/sq-popover}}
```

```example
@title Close on Click Example
@description Click the popover to call the `close` action.

@controller-properties
{
  isActive: true,
  actions: {
    close() {
      this.set('isActive', false);
    }
  }
}

@hbs
{{#sq-popover class="pointer--right"
              isActive=isActive
              closePopoverOnClick=true
              close=(action "close")}}
  <div class="my-custom-div">
    Contents inside
  </div>
{{/sq-popover}}
```

```example
@title Dropdown Example
@description <a href="/components/glass/sq-dropdown.html">sq-dropdown</a> uses contextual components,
and the popover component is `sq-popover`.

@hbs
{{#sq-dropdown as |dropdown|}}
  {{#dropdown.trigger class="button caret-down"}}
    Options
  {{/dropdown.trigger}}
  {{#dropdown.popover class="popover--fly-down"}}
    <div class="popover-list__item hyperlink">Option 1</div>
    <div class="popover-list__item hyperlink">Option 2</div>
    <div class="popover-list__item hyperlink">Option 3</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Tooltip Example
@description <a href="/components/glass/sq-tooltip.html">sq-tooltip</a> uses `sq-popover` internally.
Use `popoverClasses` to apply classes to the popover.

@hbs
{{#sq-tooltip class="icon-info l-invoices-key-stats--info-tooltip action"
                    popoverClasses="l-invoice-popover l-invoice-popover--key-stats-info"}}
  <p>This is a popover</p>
{{/sq-tooltip}}
```

## Rendering the popover within and on top of modals or other tight spaces:
If the popover being rendered is being constrained inside a parent container that's not large enough for the popover,
set `constrainInParent=false` and `placement` to any of `['top', 'top-start', 'top-end', 'left',
'left-start', 'left-end', 'right', 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end']`.

This leverages [PopperJS](https://popper.js.org/popper-documentation.html)'s positioning engine with `position: fixed` to
position the popover on top of everything irrespective of its parents. One particular disadvantage of this is that scrolling
on the popover will not properly scroll its parent container if the popover is within its own scrolling container.
(Eg. If the reference element is within a sheet layout, it will scroll the root container's scroll bar, as fixed
positioning essentially treats the popover as having no parent)

**Important notes when constrainInParent is false:**
1. The width and heights must not rely on any parent, as its position is now fixed.
2. Do not use any unintended positional classes such as "popover--fly-up" or "popover--align-right" as
they are incompatible with PopperJS.
3. Popovers are constrained within the viewport

```example
@title Position the Popover Relative to a Reference Element
@description This example sets constrainInParent to false to position the popper in a fixed position with reference to
the element specified. The popover appears from the top left corner of the reference element.

@hbs
<button id="reference-element" class="button">Reference Button</button>
{{#sq-popover class="popover-list"
              isActive=true
              constrainInParent=false
              placement="bottom-start"
              referenceElement="#reference-element"}}
  <div class="popover-list__item">this is a popover list item</div>
  <div class="popover-list__item">this is another popover list item</div>
{{/sq-popover}}
```

### Parameters
 * @param {String} [isActive=false] - Whether to show the popover
 * @param {String} [isFlipped=false] - Whether the popover has been flipped from it's original orientation in order to keep in the viewport. Currently only set via Popper when `constrainInViewport` is set to `false`, and `flip` is enabled in `extraPopperModifiers`.
 * @param {Boolean} [closePopoverOnClick=false] - Whether clicking the popover sends the "close" action
 * @param {Function} [close] - Action to call when popover is clicked and closePopoverOnClick is true
 * @param {Boolean} [constrainInParent=true] - Whether the popover should be constrained in its parent
 * @param {Boolean} [constrainInViewport=true] - Whether the popover should be constrained in its viewport
 * @param {String} [placement='bottom'] - PopperJS placement string. Required when constainInParent is false. [PopperJS default placements](https://popper.js.org/popper-documentation.html#Popper.Defaults.placement).
 * @param {DOM selector} [referenceElement=element.parent] - Element the popover is positioned relative to when constainInParent is false. Defaults to this element's parent element.
 * @param {Object} [extraPopperModifiers={}] - modifiers to add to Popper. [Popper modifiers](https://popper.js.org/popper-documentation.html#modifiers)
*/

import { assert } from '@ember/debug';
import { computed } from '@ember/object';
import { run } from '@ember/runloop';
import Component from '@ember/component';
import Popper from 'popper.js';
export const ANIMATION_DELAY_MS = 300;
const POPPER_PLACEMENTS_TOP = new Set(['top', 'top-start', 'top-end']);
const POPPER_PLACEMENTS_LEFT = new Set(['left', 'left-start', 'left-end']);
const POPPER_PLACEMENTS_RIGHT = new Set(['right', 'right-start', 'right-end']);
const POPPER_PLACEMENTS_BOTTOM = new Set(['bottom', 'bottom-start', 'bottom-end']);
export function getElement(elementOrString) {
  return elementOrString instanceof HTMLElement ? elementOrString : document.querySelector(elementOrString);
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['popover'],
  classNameBindings: ['isActive:popover--is-active', 'isFlipped:popover--is-flipped', 'fixedPlacementStyleClass'],
  // Attributes
  isActive: false,
  isFlipped: false,
  closePopoverOnClick: false,
  constrainInParent: true,
  constrainInViewport: true,
  // These attributes only apply when passing constrainInParent=false
  placement: 'bottom',
  referenceElement: null,
  popper: null,
  extraPopperModifiers: null,
  didRender(...args) {
    this._super(...args);
    if (this.get('constrainInParent') === false) {
      if (this.get('isActive')) {
        this.set('popoverIsVisible', true);
        let popper = this.get('popper');
        if (popper) {
          popper.scheduleUpdate();
        } else {
          popper = this.createPopper();
          this.set('popper', popper);
          if (this.get('onCreatePopper')) {
            this.get('onCreatePopper')(popper);
          }
        }
      }
    }
  },
  // Add margins to place the popover away from the referenceElement based on the given placement.
  // https://popper.js.org/popper-documentation.html#Popper.placements
  // sq-popover does not support the 'auto' placement string in PopperJS
  fixedPlacementStyleClass: computed('constrainInParent', 'placement', function () {
    if (this.get('constrainInParent') === false) {
      const placement = this.get('placement');
      if (POPPER_PLACEMENTS_TOP.has(placement)) {
        return 'popover--position-fixed-top';
      } else if (POPPER_PLACEMENTS_LEFT.has(placement)) {
        return 'popover--position-fixed-left';
      } else if (POPPER_PLACEMENTS_RIGHT.has(placement)) {
        return 'popover--position-fixed-right';
      } else if (POPPER_PLACEMENTS_BOTTOM.has(placement)) {
        return 'popover--position-fixed-bottom';
      } else {
        assert(`A valid placement direction (top, left, right, bottom) must be specified. placement: ${this.get('placement')}`);
        return null;
      }
    } else {
      return '';
    }
  }),
  willDestroy(...args) {
    this._super(...args);
    this.destroyPopper();
  },
  destroyPopper() {
    const popper = this.get('popper');
    if (popper) {
      popper.destroy();
      this.set('popper', null);
      if (this.get('onCreatePopper')) {
        this.get('onCreatePopper')(null);
      }
    }
  },
  createPopper() {
    const popperReferenceElement = getElement(this.get('referenceElement')) || this.get('element').parentElement;
    const extraPopperModifiers = this.extraPopperModifiers || {};
    return new Popper(popperReferenceElement, this.get('element'), {
      placement: this.get('placement'),
      positionFixed: true,
      modifiers: {
        preventOverflow: {
          // Always constrain within the viewport
          enabled: this.constrainInViewport,
          boundariesElement: 'viewport'
        },
        hide: {
          // Hide should also be disabled if prevent overflow is disabled
          enabled: false
        },
        flip: {
          // Don't try to flip the popper if it will overflow the screen
          enabled: false
        },
        computeStyle: {
          // Use the top and left properties to position the popover.
          gpuAcceleration: false
        },
        ...extraPopperModifiers
      },
      onCreate: dataObject => {
        run(() => this.set('isFlipped', dataObject.flipped));
      },
      onUpdate: dataObject => {
        run(() => this.set('isFlipped', dataObject.flipped));
      }
    });
  },
  click() {
    if (this.get('closePopoverOnClick') && this.close) {
      this.close();
    }
  }
});